import React, { useCallback, useMemo, useState, type ReactNode } from "react";
import { EditTeamRequest, TeamAccess, TeamDetails } from "target_wasm";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { useNavigation } from "@/contexts/NavigationProvider";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import {
  TeamMember,
  deleteTeam,
  editTeam,
  leaveTeam,
  leaveTeamPreview,
  PreviewUnavailableError,
} from "./TeamsApi";
import {
  formatImpactSentence,
  LeaveTeamImpactPanel,
  type LeavePreviewState,
} from "./LeaveTeamImpactPanel";

export interface TeamActionPermissions {
  isAdmin: boolean;
  /** Admin but the only remaining admin — cannot leave until another is named. */
  isLastAdmin: boolean;
  canDelete: boolean;
}

export interface TeamActions {
  permissions: TeamActionPermissions;
  /** Rename via the edit-team endpoint; returns false on validation failure. */
  commitName: (name: string) => boolean;
  /** Persist a color/asset (appearance) change. */
  editAppearance: (updates: { color?: string; asset?: string }) => void;
  requestLeave: () => void;
  requestDelete: () => void;
  /** Leave / delete confirm dialog (with leave-impact preview) — render once. */
  dialogs: ReactNode;
}

/**
 * Shared team-management logic — rename, appearance, leave (with the
 * stop-syncing impact preview) and delete + permission derivations + the
 * confirm dialog. Lifted out of the old `TeamDetailsSidebar` so both the teams
 * list rows and the single-team header reuse one implementation.
 */
export function useTeamActions(
  team: TeamAccess | null,
  members: TeamMember[],
): TeamActions {
  const { navigateToPage, refreshTeams } = useNavigation();
  const updateTeamInStore = useTeamsStore((s) => s.updateTeam);
  const removeTeamFromStore = useTeamsStore((s) => s.removeTeam);

  const [dialog, setDialog] = useState<{
    open: boolean;
    action: "leave" | "delete" | null;
  }>({ open: false, action: null });
  const [isLoading, setIsLoading] = useState(false);
  const [leavePreview, setLeavePreview] = useState<LeavePreviewState>({
    status: "idle",
  });

  const isAdmin = useMemo(() => {
    try {
      return team?.relation_display_js() === "Admin";
    } catch {
      return false;
    }
  }, [team]);
  const adminCount = useMemo(
    () =>
      members.filter((m) => m.accessLevel === "Admin" && !m.isPending).length,
    [members],
  );
  const isLastAdmin = isAdmin && adminCount <= 1;

  const teamId = team?.team_id_str() ?? "";
  const teamName = team ? String(team.team_details_js().team_name_js()) : "";

  const editDetails = useCallback(
    async (updates: { name?: string; color?: string; asset?: string }) => {
      if (!team) return;
      const d = team.team_details_js();
      const newDetails = new TeamDetails(
        updates.name?.trim() || d.team_name_js(),
        d.team_description_js() ?? "",
        updates.color ?? d.team_color_js(),
        updates.asset ?? d.team_asset_js(),
        d.created_at_js(),
      );
      try {
        const updated = await editTeam(
          EditTeamRequest.new_js(teamId, newDetails),
        );
        updateTeamInStore(updated);
        refreshTeams();
      } catch (err: unknown) {
        showNotificationGlobal(
          err instanceof Error ? err.message : "Failed to update team",
          "error",
        );
      }
    },
    [team, teamId, updateTeamInStore, refreshTeams],
  );

  const commitName = useCallback(
    (name: string): boolean => {
      const trimmed = name.trim();
      if (trimmed === teamName) return true;
      if (!trimmed) {
        showNotificationGlobal("Team name cannot be empty", "error");
        return false;
      }
      if (trimmed.length < 3) {
        showNotificationGlobal(
          "Team name must be at least 3 characters",
          "error",
        );
        return false;
      }
      void editDetails({ name: trimmed });
      return true;
    },
    [teamName, editDetails],
  );

  const editAppearance = useCallback(
    (updates: { color?: string; asset?: string }) => void editDetails(updates),
    [editDetails],
  );

  const cancel = useCallback(() => {
    setDialog({ open: false, action: null });
    setLeavePreview({ status: "idle" });
  }, []);

  const requestLeave = useCallback(() => {
    if (isLastAdmin) {
      showNotificationGlobal(
        "You cannot leave the team as you are the last admin.",
        "error",
      );
      return;
    }
    setDialog({ open: true, action: "leave" });
    setLeavePreview({ status: "loading" });
    leaveTeamPreview(teamId)
      .then((data) => setLeavePreview({ status: "loaded", data }))
      .catch((err) => {
        const reason =
          err instanceof PreviewUnavailableError ? err.reason : "unknown";
        setLeavePreview({ status: "error", reason });
      });
  }, [isLastAdmin, teamId]);

  const requestDelete = useCallback(() => {
    setDialog({ open: true, action: "delete" });
    setLeavePreview({ status: "idle" });
  }, []);

  const confirm = useCallback(async () => {
    if (!dialog.action) return;
    setIsLoading(true);
    try {
      if (dialog.action === "leave") {
        await leaveTeam(teamId);
        showNotificationGlobal("You have left the team", "success");
      } else {
        await deleteTeam(teamId);
        showNotificationGlobal("Team deleted successfully", "success");
      }
      removeTeamFromStore(teamId);
      void useMainStateStore.getState().invalidateModels();
      refreshTeams();
      navigateToPage("Teams");
    } catch {
      showNotificationGlobal(
        dialog.action === "leave"
          ? "Failed to leave the team"
          : "Failed to delete the team",
        "error",
      );
    } finally {
      setIsLoading(false);
      cancel();
    }
  }, [
    dialog.action,
    teamId,
    removeTeamFromStore,
    refreshTeams,
    navigateToPage,
    cancel,
  ]);

  const tablesAffected =
    leavePreview.status === "loaded" ? leavePreview.data.tables_affected : 0;

  const dialogs = (
    <ConfirmDialog
      open={dialog.open}
      title={
        dialog.action === "delete"
          ? `Delete ${teamName}?`
          : `Leave ${teamName}?`
      }
      message={
        dialog.action === "delete" ? (
          `This can't be undone.`
        ) : (
          <>
            <div>
              You&apos;ll lose access to the team and its models.
              {tablesAffected > 0 && leavePreview.status === "loaded" ? (
                <>
                  {" "}
                  <span className="text-fg-semibold">
                    {formatImpactSentence(leavePreview.data)}
                  </span>
                </>
              ) : null}
            </div>
            <LeaveTeamImpactPanel preview={leavePreview} />
          </>
        )
      }
      confirmText={dialog.action === "delete" ? "Delete" : "Leave"}
      confirmVariant={
        dialog.action === "delete" || tablesAffected > 0
          ? "destructive"
          : "primary"
      }
      onConfirm={confirm}
      onCancel={cancel}
      isLoading={isLoading}
    />
  );

  return {
    permissions: { isAdmin, isLastAdmin, canDelete: isAdmin },
    commitName,
    editAppearance,
    requestLeave,
    requestDelete,
    dialogs,
  };
}
